import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Typography,
  Switch,
  FormControlLabel,
  Grid,
  Alert,
  Snackbar,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  Stepper,
  Step,
  StepLabel,
  StepContent
} from '@mui/material';
import {
  Notifications as NotificationsIcon,
  Email as EmailIcon,
  Save as SaveIcon,
  Refresh as RefreshIcon,
  Settings as SettingsIcon
} from '@mui/icons-material';
import apiService from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';

/**
 * NotificationSettings component with SafeLine theme matching verification pages
 */
const NotificationSettings = () => {
  // SafeLine Pickup Color Scheme
  const safeLineColors = {
    flameOrange: '#ff6b35',
    brightOrange: '#ff9500', 
    deepOrange: '#e55100',
    orangeTint: '#fff4f0',
    pureWhite: '#ffffff',
    lightGray: '#f8f9fa',
    coolGray: '#e9ecef',
    mediumGray: '#dee2e6',
    emerald: '#10b981',
    lightEmerald: '#d1fae5',
    forestGreen: '#047857',
    amber: '#f59e0b',
    lightAmber: '#fef3c7',
    darkAmber: '#d97706',
    red: '#ef4444',
    lightRed: '#fee2e2',
    darkRed: '#dc2626',
    darkCharcoal: '#1f2937',
    mediumCharcoal: '#374151',
    lightCharcoal: '#6b7280',
    mutedGray: '#9ca3af'
  };

  const [settings, setSettings] = useState({
    // General notifications
    enableNotifications: true,
    enableSounds: true,
    enableVibrations: false,
    
    // Email notifications
    enableEmailNotifications: true,
    emailFrequency: 'immediate',
    emailDigest: false,
    digestTime: '18:00',
    
    // SMS notifications
    enableSMSNotifications: false,
    smsFrequency: 'urgent_only',
    smsPhoneNumber: '',
    
    // Push notifications
    enablePushNotifications: true,
    pushFrequency: 'all',
    
    // Specific notification types
    pickupNotifications: true,
    guestNotifications: true,
    securityAlerts: true,
    systemUpdates: false,
    maintenanceAlerts: true,
    reportNotifications: false,
    
    // Quiet hours
    enableQuietHours: false,
    quietHoursStart: '22:00',
    quietHoursEnd: '07:00',
    
    // Priority settings
    highPriorityOnly: false,
    emergencyOverride: true
  });

  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [loadingInitial, setLoadingInitial] = useState(true);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  // Load settings on component mount
  useEffect(() => {
    const loadSettings = async () => {
      try {
        setLoadingInitial(true);
        const userId = user?.id;
        
        if (userId) {
          const userSettings = await apiService.settings.getByCategory(userId, 'notifications');
          if (userSettings && Object.keys(userSettings).length > 0) {
            setSettings(userSettings);
            console.log('Loaded notification settings:', userSettings);
          }
        }
      } catch (error) {
        console.error('Error loading notification settings:', error);
        setSnackbar({
          open: true,
          message: 'Error loading settings. Using defaults.',
          severity: 'warning'
        });
      } finally {
        setLoadingInitial(false);
      }
    };

    loadSettings();
  }, [user]);

  const handleSettingChange = (key, value) => {
    setSettings(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleSave = async () => {
    if (!user?.id) {
      setSnackbar({
        open: true,
        message: 'Error: User not authenticated',
        severity: 'error'
      });
      return;
    }

    setLoading(true);
    try {
      await apiService.settings.save(user.id, 'notifications', settings);
      setSnackbar({
        open: true,
        message: 'Notification settings saved successfully!',
        severity: 'success'
      });
    } catch (error) {
      console.error('Error saving notification settings:', error);
      setSnackbar({
        open: true,
        message: 'Error saving settings. Please try again.',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleReset = async () => {
    if (!user?.id) {
      setSnackbar({
        open: true,
        message: 'Error: User not authenticated',
        severity: 'error'
      });
      return;
    }

    setLoading(true);
    try {
      const defaultSettings = await apiService.settings.reset(user.id, 'notifications');
      setSettings(defaultSettings.notifications);
      setSnackbar({
        open: true,
        message: 'Settings reset to defaults',
        severity: 'info'
      });
    } catch (error) {
      console.error('Error resetting notification settings:', error);
      setSnackbar({
        open: true,
        message: 'Error resetting settings. Please try again.',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const settingsSteps = [
    'General Settings',
    'Email & SMS',
    'Notification Types',
    'Quiet Hours'
  ];

  return (
    <Box sx={{}}>
      <Grid container spacing={2}>
        {/* Settings Process Guide */}
        <Grid item xs={12} lg={4}>
          <Card
            elevation={0}
            sx={{
              borderRadius: 3,
              backgroundColor: safeLineColors.pureWhite,
              border: `1px solid ${safeLineColors.coolGray}`,
              overflow: 'hidden',
              position: 'relative',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: '4px',
                background: `linear-gradient(90deg, ${safeLineColors.flameOrange}, ${safeLineColors.brightOrange})`
              }
            }}
          >
            <CardHeader
              title={
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <NotificationsIcon sx={{ color: safeLineColors.flameOrange, mr: 1.5 }} />
                  <Typography variant="h6" sx={{ color: safeLineColors.darkCharcoal, fontWeight: 'bold' }}>
                    Notification Guide
                  </Typography>
                </Box>
              }
              sx={{ 
                backgroundColor: safeLineColors.orangeTint,
                borderBottom: `1px solid ${safeLineColors.coolGray}`,
                pb: 2,
                pt: 2
              }}
            />
            <CardContent>
              <Stepper orientation="vertical">
                {settingsSteps.map((step, index) => (
                  <Step key={step} active={true}>
                    <StepLabel>
                      <Typography sx={{ color: safeLineColors.darkCharcoal, fontWeight: 'medium' }}>
                        {step}
                      </Typography>
                    </StepLabel>
                    <StepContent>
                      <Typography variant="body2" sx={{ color: safeLineColors.mediumCharcoal }}>
                        {index === 0 && 'Configure general notification preferences and sounds'}
                        {index === 1 && 'Set up email and SMS notification delivery'}
                        {index === 2 && 'Choose which types of notifications to receive'}
                        {index === 3 && 'Configure quiet hours and priority settings'}
                      </Typography>
                    </StepContent>
                  </Step>
                ))}
              </Stepper>
            </CardContent>
          </Card>
        </Grid>

        {/* Settings Form */}
        <Grid item xs={12} lg={8}>
          <Card
            elevation={0}
            sx={{
              borderRadius: 3,
              backgroundColor: safeLineColors.pureWhite,
              border: `1px solid ${safeLineColors.coolGray}`,
              overflow: 'hidden',
              position: 'relative',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: '4px',
                background: `linear-gradient(90deg, ${safeLineColors.flameOrange}, ${safeLineColors.brightOrange})`
              }
            }}
          >
            <CardHeader
              title={
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Box sx={{ 
                      p: 1.5, 
                      borderRadius: 2,
                      backgroundColor: safeLineColors.flameOrange,
                      mr: 2
                    }}>
                      <NotificationsIcon sx={{ color: safeLineColors.pureWhite, fontSize: 24 }} />
                    </Box>
                    <Box>
                      <Typography variant="h6" sx={{ 
                        fontWeight: 'bold', 
                        color: safeLineColors.darkCharcoal 
                      }}>
                        Notification Settings
                      </Typography>
                      <Typography variant="body2" sx={{ 
                        color: safeLineColors.mediumCharcoal 
                      }}>
                        Configure how and when you receive notifications
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              }
              sx={{ 
                backgroundColor: safeLineColors.orangeTint,
                borderBottom: `1px solid ${safeLineColors.coolGray}`,
                pb: 2,
                pt: 2
              }}
            />
            
            <CardContent sx={{ p: 3 }}>
              {/* General Notifications */}
              <Box sx={{ mb: 4 }}>
                <Typography variant="h6" sx={{ 
                  color: safeLineColors.darkCharcoal, 
                  fontWeight: 'medium',
                  mb: 2,
                  display: 'flex',
                  alignItems: 'center'
                }}>
                  <SettingsIcon sx={{ color: safeLineColors.flameOrange, mr: 1, fontSize: 20 }} />
                  General Notifications
                </Typography>
                
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={settings.enableNotifications}
                            onChange={(e) => handleSettingChange('enableNotifications', e.target.checked)}
                            sx={{
                              '& .MuiSwitch-switchBase.Mui-checked': {
                                color: safeLineColors.flameOrange,
                              },
                              '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                backgroundColor: safeLineColors.flameOrange,
                              },
                            }}
                          />
                        }
                        label={
                          <Typography sx={{ color: safeLineColors.darkCharcoal }}>
                            Enable Notifications
                          </Typography>
                        }
                      />
                      <FormControlLabel
                        control={
                          <Switch
                            checked={settings.enableSounds}
                            onChange={(e) => handleSettingChange('enableSounds', e.target.checked)}
                            sx={{
                              '& .MuiSwitch-switchBase.Mui-checked': {
                                color: safeLineColors.flameOrange,
                              },
                              '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                backgroundColor: safeLineColors.flameOrange,
                              },
                            }}
                          />
                        }
                        label={
                          <Typography sx={{ color: safeLineColors.darkCharcoal }}>
                            Enable Sounds
                          </Typography>
                        }
                      />
                      <FormControlLabel
                        control={
                          <Switch
                            checked={settings.enableVibrations}
                            onChange={(e) => handleSettingChange('enableVibrations', e.target.checked)}
                            sx={{
                              '& .MuiSwitch-switchBase.Mui-checked': {
                                color: safeLineColors.flameOrange,
                              },
                              '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                backgroundColor: safeLineColors.flameOrange,
                              },
                            }}
                          />
                        }
                        label={
                          <Typography sx={{ color: safeLineColors.darkCharcoal }}>
                            Enable Vibrations
                          </Typography>
                        }
                      />
                    </Box>
                  </Grid>
                  
                  <Grid item xs={12} md={6}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={settings.highPriorityOnly}
                            onChange={(e) => handleSettingChange('highPriorityOnly', e.target.checked)}
                            sx={{
                              '& .MuiSwitch-switchBase.Mui-checked': {
                                color: safeLineColors.flameOrange,
                              },
                              '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                backgroundColor: safeLineColors.flameOrange,
                              },
                            }}
                          />
                        }
                        label={
                          <Typography sx={{ color: safeLineColors.darkCharcoal }}>
                            High Priority Only
                          </Typography>
                        }
                      />
                      <FormControlLabel
                        control={
                          <Switch
                            checked={settings.emergencyOverride}
                            onChange={(e) => handleSettingChange('emergencyOverride', e.target.checked)}
                            sx={{
                              '& .MuiSwitch-switchBase.Mui-checked': {
                                color: safeLineColors.flameOrange,
                              },
                              '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                backgroundColor: safeLineColors.flameOrange,
                              },
                            }}
                          />
                        }
                        label={
                          <Typography sx={{ color: safeLineColors.darkCharcoal }}>
                            Emergency Override
                          </Typography>
                        }
                      />
                    </Box>
                  </Grid>
                </Grid>
              </Box>

              <Divider sx={{ borderColor: safeLineColors.coolGray, my: 3 }} />

              {/* Email & SMS Notifications */}
              <Box sx={{ mb: 4 }}>
                <Typography variant="h6" sx={{ 
                  color: safeLineColors.darkCharcoal, 
                  fontWeight: 'medium',
                  mb: 2,
                  display: 'flex',
                  alignItems: 'center'
                }}>
                  <EmailIcon sx={{ color: safeLineColors.flameOrange, mr: 1, fontSize: 20 }} />
                  Email & SMS Notifications
                </Typography>
                
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle1" sx={{ 
                      color: safeLineColors.darkCharcoal,
                      fontWeight: 'medium',
                      mb: 2
                    }}>
                      Email Settings
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={settings.enableEmailNotifications}
                            onChange={(e) => handleSettingChange('enableEmailNotifications', e.target.checked)}
                            sx={{
                              '& .MuiSwitch-switchBase.Mui-checked': {
                                color: safeLineColors.flameOrange,
                              },
                              '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                backgroundColor: safeLineColors.flameOrange,
                              },
                            }}
                          />
                        }
                        label={
                          <Typography sx={{ color: safeLineColors.darkCharcoal }}>
                            Enable Email Notifications
                          </Typography>
                        }
                      />
                      <FormControlLabel
                        control={
                          <Switch
                            checked={settings.emailDigest}
                            onChange={(e) => handleSettingChange('emailDigest', e.target.checked)}
                            sx={{
                              '& .MuiSwitch-switchBase.Mui-checked': {
                                color: safeLineColors.flameOrange,
                              },
                              '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                backgroundColor: safeLineColors.flameOrange,
                              },
                            }}
                          />
                        }
                        label={
                          <Typography sx={{ color: safeLineColors.darkCharcoal }}>
                            Daily Digest
                          </Typography>
                        }
                      />
                    </Box>
                    
                    {settings.enableEmailNotifications && (
                      <FormControl fullWidth sx={{ mt: 2 }}>
                        <InputLabel>Email Frequency</InputLabel>
                        <Select
                          value={settings.emailFrequency}
                          onChange={(e) => handleSettingChange('emailFrequency', e.target.value)}
                          label="Email Frequency"
                          sx={{
                            borderRadius: 2,
                            '& .MuiOutlinedInput-notchedOutline': {
                              borderColor: safeLineColors.coolGray,
                            },
                            '&:hover .MuiOutlinedInput-notchedOutline': {
                              borderColor: safeLineColors.flameOrange,
                            },
                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                              borderColor: safeLineColors.flameOrange,
                            }
                          }}
                        >
                          <MenuItem value="immediate">Immediate</MenuItem>
                          <MenuItem value="hourly">Hourly</MenuItem>
                          <MenuItem value="daily">Daily</MenuItem>
                        </Select>
                      </FormControl>
                    )}
                  </Grid>
                  
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle1" sx={{ 
                      color: safeLineColors.darkCharcoal,
                      fontWeight: 'medium',
                      mb: 2
                    }}>
                      SMS Settings
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={settings.enableSMSNotifications}
                            onChange={(e) => handleSettingChange('enableSMSNotifications', e.target.checked)}
                            sx={{
                              '& .MuiSwitch-switchBase.Mui-checked': {
                                color: safeLineColors.flameOrange,
                              },
                              '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                backgroundColor: safeLineColors.flameOrange,
                              },
                            }}
                          />
                        }
                        label={
                          <Typography sx={{ color: safeLineColors.darkCharcoal }}>
                            Enable SMS Notifications
                          </Typography>
                        }
                      />
                    </Box>
                    
                    {settings.enableSMSNotifications && (
                      <>
                        <TextField
                          fullWidth
                          label="SMS Phone Number"
                          value={settings.smsPhoneNumber}
                          onChange={(e) => handleSettingChange('smsPhoneNumber', e.target.value)}
                          variant="outlined"
                          sx={{ mt: 2,
                            '& .MuiOutlinedInput-root': {
                              borderRadius: 2,
                              '& fieldset': {
                                borderColor: safeLineColors.coolGray,
                              },
                              '&:hover fieldset': {
                                borderColor: safeLineColors.flameOrange,
                              },
                              '&.Mui-focused fieldset': {
                                borderColor: safeLineColors.flameOrange,
                              }
                            }
                          }}
                        />
                        <FormControl fullWidth sx={{ mt: 2 }}>
                          <InputLabel>SMS Frequency</InputLabel>
                          <Select
                            value={settings.smsFrequency}
                            onChange={(e) => handleSettingChange('smsFrequency', e.target.value)}
                            label="SMS Frequency"
                            sx={{
                              borderRadius: 2,
                              '& .MuiOutlinedInput-notchedOutline': {
                                borderColor: safeLineColors.coolGray,
                              },
                              '&:hover .MuiOutlinedInput-notchedOutline': {
                                borderColor: safeLineColors.flameOrange,
                              },
                              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                borderColor: safeLineColors.flameOrange,
                              }
                            }}
                          >
                            <MenuItem value="urgent_only">Urgent Only</MenuItem>
                            <MenuItem value="all">All Notifications</MenuItem>
                            <MenuItem value="daily">Daily Summary</MenuItem>
                          </Select>
                        </FormControl>
                      </>
                    )}
                  </Grid>
                </Grid>
              </Box>

              <Divider sx={{ borderColor: safeLineColors.coolGray, my: 3 }} />

              {/* Notification Types */}
              <Box sx={{ mb: 4 }}>
                <Typography variant="h6" sx={{ 
                  color: safeLineColors.darkCharcoal, 
                  fontWeight: 'medium',
                  mb: 2,
                  display: 'flex',
                  alignItems: 'center'
                }}>
                  Notification Types
                </Typography>
                
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle1" sx={{ 
                      color: safeLineColors.darkCharcoal,
                      fontWeight: 'medium',
                      mb: 2
                    }}>
                      Safety & Security
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={settings.pickupNotifications}
                            onChange={(e) => handleSettingChange('pickupNotifications', e.target.checked)}
                            sx={{
                              '& .MuiSwitch-switchBase.Mui-checked': {
                                color: safeLineColors.flameOrange,
                              },
                              '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                backgroundColor: safeLineColors.flameOrange,
                              },
                            }}
                          />
                        }
                        label={
                          <Typography sx={{ color: safeLineColors.darkCharcoal }}>
                            Pickup Notifications
                          </Typography>
                        }
                      />
                      <FormControlLabel
                        control={
                          <Switch
                            checked={settings.guestNotifications}
                            onChange={(e) => handleSettingChange('guestNotifications', e.target.checked)}
                            sx={{
                              '& .MuiSwitch-switchBase.Mui-checked': {
                                color: safeLineColors.flameOrange,
                              },
                              '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                backgroundColor: safeLineColors.flameOrange,
                              },
                            }}
                          />
                        }
                        label={
                          <Typography sx={{ color: safeLineColors.darkCharcoal }}>
                            Guest Notifications
                          </Typography>
                        }
                      />
                      <FormControlLabel
                        control={
                          <Switch
                            checked={settings.securityAlerts}
                            onChange={(e) => handleSettingChange('securityAlerts', e.target.checked)}
                            sx={{
                              '& .MuiSwitch-switchBase.Mui-checked': {
                                color: safeLineColors.flameOrange,
                              },
                              '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                backgroundColor: safeLineColors.flameOrange,
                              },
                            }}
                          />
                        }
                        label={
                          <Typography sx={{ color: safeLineColors.darkCharcoal }}>
                            Security Alerts
                          </Typography>
                        }
                      />
                    </Box>
                  </Grid>
                  
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle1" sx={{ 
                      color: safeLineColors.darkCharcoal,
                      fontWeight: 'medium',
                      mb: 2
                    }}>
                      System & Reports
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={settings.systemUpdates}
                            onChange={(e) => handleSettingChange('systemUpdates', e.target.checked)}
                            sx={{
                              '& .MuiSwitch-switchBase.Mui-checked': {
                                color: safeLineColors.flameOrange,
                              },
                              '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                backgroundColor: safeLineColors.flameOrange,
                              },
                            }}
                          />
                        }
                        label={
                          <Typography sx={{ color: safeLineColors.darkCharcoal }}>
                            System Updates
                          </Typography>
                        }
                      />
                      <FormControlLabel
                        control={
                          <Switch
                            checked={settings.maintenanceAlerts}
                            onChange={(e) => handleSettingChange('maintenanceAlerts', e.target.checked)}
                            sx={{
                              '& .MuiSwitch-switchBase.Mui-checked': {
                                color: safeLineColors.flameOrange,
                              },
                              '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                backgroundColor: safeLineColors.flameOrange,
                              },
                            }}
                          />
                        }
                        label={
                          <Typography sx={{ color: safeLineColors.darkCharcoal }}>
                            Maintenance Alerts
                          </Typography>
                        }
                      />
                      <FormControlLabel
                        control={
                          <Switch
                            checked={settings.reportNotifications}
                            onChange={(e) => handleSettingChange('reportNotifications', e.target.checked)}
                            sx={{
                              '& .MuiSwitch-switchBase.Mui-checked': {
                                color: safeLineColors.flameOrange,
                              },
                              '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                backgroundColor: safeLineColors.flameOrange,
                              },
                            }}
                          />
                        }
                        label={
                          <Typography sx={{ color: safeLineColors.darkCharcoal }}>
                            Report Notifications
                          </Typography>
                        }
                      />
                    </Box>
                  </Grid>
                </Grid>
              </Box>

              <Divider sx={{ borderColor: safeLineColors.coolGray, my: 3 }} />

              {/* Quiet Hours */}
              <Box sx={{ mb: 4 }}>
                <Typography variant="h6" sx={{ 
                  color: safeLineColors.darkCharcoal, 
                  fontWeight: 'medium',
                  mb: 2,
                  display: 'flex',
                  alignItems: 'center'
                }}>
                  Quiet Hours
                </Typography>
                
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={settings.enableQuietHours}
                          onChange={(e) => handleSettingChange('enableQuietHours', e.target.checked)}
                          sx={{
                            '& .MuiSwitch-switchBase.Mui-checked': {
                              color: safeLineColors.flameOrange,
                            },
                            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                              backgroundColor: safeLineColors.flameOrange,
                            },
                          }}
                        />
                      }
                      label={
                        <Typography sx={{ color: safeLineColors.darkCharcoal }}>
                          Enable Quiet Hours
                        </Typography>
                      }
                    />
                  </Grid>
                  
                  {settings.enableQuietHours && (
                    <>
                      <Grid item xs={12} md={3}>
                        <TextField
                          fullWidth
                          label="Start Time"
                          type="time"
                          value={settings.quietHoursStart}
                          onChange={(e) => handleSettingChange('quietHoursStart', e.target.value)}
                          variant="outlined"
                          InputLabelProps={{ shrink: true }}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              borderRadius: 2,
                              '& fieldset': {
                                borderColor: safeLineColors.coolGray,
                              },
                              '&:hover fieldset': {
                                borderColor: safeLineColors.flameOrange,
                              },
                              '&.Mui-focused fieldset': {
                                borderColor: safeLineColors.flameOrange,
                              }
                            }
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <TextField
                          fullWidth
                          label="End Time"
                          type="time"
                          value={settings.quietHoursEnd}
                          onChange={(e) => handleSettingChange('quietHoursEnd', e.target.value)}
                          variant="outlined"
                          InputLabelProps={{ shrink: true }}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              borderRadius: 2,
                              '& fieldset': {
                                borderColor: safeLineColors.coolGray,
                              },
                              '&:hover fieldset': {
                                borderColor: safeLineColors.flameOrange,
                              },
                              '&.Mui-focused fieldset': {
                                borderColor: safeLineColors.flameOrange,
                              }
                            }
                          }}
                        />
                      </Grid>
                    </>
                  )}
                </Grid>
              </Box>

              {/* Actions */}
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 4 }}>
                <Button
                  variant="outlined"
                  startIcon={<RefreshIcon />}
                  onClick={handleReset}
                  disabled={loading}
                  sx={{
                    borderColor: safeLineColors.flameOrange,
                    color: safeLineColors.flameOrange,
                    fontWeight: 'medium',
                    textTransform: 'none',
                    borderRadius: 2,
                    px: 3,
                    py: 1.25,
                    '&:hover': {
                      backgroundColor: safeLineColors.orangeTint,
                      borderColor: safeLineColors.brightOrange
                    }
                  }}
                >
                  Reset
                </Button>
                <Button
                  variant="contained"
                  startIcon={<SaveIcon />}
                  onClick={handleSave}
                  disabled={loading}
                  sx={{
                    background: `linear-gradient(135deg, ${safeLineColors.flameOrange}, ${safeLineColors.brightOrange})`,
                    fontWeight: 'medium',
                    textTransform: 'none',
                    borderRadius: 2,
                    px: 3,
                    py: 1.25,
                    '&:hover': {
                      background: `linear-gradient(135deg, ${safeLineColors.brightOrange}, ${safeLineColors.flameOrange})`
                    }
                  }}
                >
                  Save Changes
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default NotificationSettings; 